import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { errMsg, saveAuth } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ComuneAutocomplete from "@/components/ComuneAutocomplete";
import StatoAutocomplete from "@/components/StatoAutocomplete";
import { ShieldCheck, Upload, Check, Camera, AlertCircle, Gift, BookOpen, Sparkles, Copy, ArrowRight, Lock } from "lucide-react";
import { validateAlloggiati } from "@/lib/alloggiatiValidator";

const PROVINCE_CODES = ["AG","AL","AN","AO","AR","AP","AT","AV","BA","BT","BL","BN","BG","BI","BO","BZ","BS","BR","CA","CL","CB","CI","CE","CT","CZ","CH","CO","CS","CR","KR","CN","EN","FM","FE","FI","FG","FC","FR","GE","GO","GR","IM","IS","AQ","SP","LT","LE","LC","LI","LO","LU","MC","MN","MS","MT","VS","ME","MI","MO","MB","NA","NO","NU","OG","OT","OR","PD","PA","PR","PV","PG","PU","PE","PC","PI","PT","PN","PZ","PO","RG","RA","RC","RE","RI","RN","RM","RO","SA","SS","SV","SI","SR","SO","TA","TE","TR","TO","TP","TN","TV","TS","UD","VA","VE","VB","VC","VR","VV","VI","VT"];

export default function SelfCheckin() {
  const { token } = useParams();
  const { lang } = useLang();
  const IT = lang === "it";
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [docTypes, setDocTypes] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [password, setPassword] = useState("");
  const [registering, setRegistering] = useState(false);
  const [promo, setPromo] = useState(null);
  const [accountCreated, setAccountCreated] = useState(false);
  const [f, setF] = useState({
    guest_type: "16",
    last_name: "", first_name: "", gender: "M", date_of_birth: "",
    comune_of_birth_code: "", province_of_birth: "",
    country_of_birth_code: "100000100", citizenship_code: "100000100",
    doc_type: "IDELE", doc_number: "", doc_place_code: "",
    doc_photo_front_url: "", doc_photo_back_url: "", selfie_url: "",
  });
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    api.get(`/self-checkin/${token}`)
      .then((r) => setInfo(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    api.get("/guests/doc-types").then((r) => setDocTypes(r.data)).catch(() => {});
  }, [token]);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const isItalianBorn = f.country_of_birth_code === "100000100";
  // Live validation for Alloggiati Web — gates the submit button and drives
  // the per-field inline error copy below.
  const { ok: isValid, errors: valErr } = validateAlloggiati(f);
  const fieldErr = (k) => valErr[k];

  const uploadPhoto = async (field, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [field]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(field, r.data.url);
      toast.success(IT ? "Foto caricata" : "Photo uploaded");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploading((u) => ({ ...u, [field]: false })); }
  };

  const submit = async () => {
    const check = validateAlloggiati(f);
    if (!check.ok) {
      const first = Object.values(check.errors)[0];
      toast.error(IT ? `Correggi i campi evidenziati · ${first}` : `Fix highlighted fields · ${first}`);
      return;
    }
    setSaving(true);
    try {
      await api.post(`/self-checkin/${token}/submit`, f);
      setSubmitted(true);
      toast.success(IT ? "Grazie! Dati registrati." : "Thank you! Data registered.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">{IT ? "Caricamento…" : "Loading…"}</div>;
  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-3"/>
        <h1 className="font-display text-2xl mb-2">{IT ? "Link non valido" : "Invalid link"}</h1>
        <p className="text-sm text-muted-foreground">{IT ? "Questo link di self check-in non è più attivo. Contatta la tua struttura." : "This self check-in link is no longer active. Contact your host."}</p>
      </div>
    </div>
  );
  if (submitted) {
    const guestEmail = info?.booking?.guest_email || "";
    const propertyName = info?.property?.name || "";
    const canRegister = info?.account_status === "none" && !!guestEmail;
    const alreadyGuest = info?.account_status === "guest";
    const otherRole = info?.account_status === "other_role";

    const doRegister = async () => {
      if (!password || password.length < 8) {
        toast.error(IT ? "La password deve essere di almeno 8 caratteri" : "Password must be at least 8 characters");
        return;
      }
      setRegistering(true);
      try {
        const r = await api.post(`/self-checkin/${token}/register-account`, { password, marketing_opt_in: true });
        saveAuth(r.data.token, r.data.user);
        setPromo(r.data.promo_code);
        setAccountCreated(true);
        toast.success(IT ? "Account creato! Sconto attivato." : "Account created! Discount unlocked.");
      } catch (e) {
        toast.error(errMsg(e));
      } finally { setRegistering(false); }
    };

    const copyCode = async () => {
      if (!promo?.code) return;
      try {
        await navigator.clipboard.writeText(promo.code);
        toast.success(IT ? "Codice copiato" : "Code copied");
      } catch { toast.error(IT ? "Impossibile copiare" : "Copy failed"); }
    };

    return (
      <div className="min-h-screen bg-gradient-to-b from-sand/40 to-background px-4 sm:px-6 py-10 sm:py-14">
        <div className="max-w-xl mx-auto space-y-6" data-testid="self-checkin-success">
          {/* Success card */}
          <div className="bg-card border border-primary/30 rounded-2xl p-8 text-center shadow-sm">
            <div className="w-16 h-16 rounded-full bg-sage/20 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-sage"/>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl mb-2">
              {IT ? "Registrazione completata!" : "Registration complete!"}
            </h1>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {IT
                ? `Grazie ${f.first_name}, i tuoi dati sono stati trasmessi in sicurezza a ${propertyName}. Buon soggiorno!`
                : `Thank you ${f.first_name}, your data has been securely sent to ${propertyName}. Enjoy your stay!`}
            </p>
          </div>

          {/* Promo code result (after account is created) */}
          {accountCreated && promo && (
            <div className="bg-primary/5 border-2 border-primary/40 rounded-2xl p-6 text-center" data-testid="sc-promo-card">
              <div className="uppercase tracking-[0.2em] text-[10px] text-primary font-semibold flex items-center justify-center gap-1.5 mb-2">
                <Sparkles className="w-3 h-3"/>{IT ? "Il tuo sconto di benvenuto" : "Your welcome discount"}
              </div>
              <div className="font-display text-2xl mb-3">{IT ? `-${promo.percent_off}% sulla prossima prenotazione` : `-${promo.percent_off}% on your next booking`}</div>
              <button
                onClick={copyCode}
                className="inline-flex items-center gap-2 bg-card border border-dashed border-primary/60 rounded-lg px-4 py-3 font-mono text-lg tracking-widest text-primary hover:bg-primary/10 transition"
                data-testid="sc-promo-copy"
              >
                {promo.code}<Copy className="w-4 h-4 opacity-60"/>
              </button>
              <p className="text-[11px] text-muted-foreground mt-2">{IT ? "Valido 6 mesi · uso singolo · ricevuto anche via email" : "Valid 6 months · single use · also sent by email"}</p>
              <Button
                onClick={() => nav("/guest/dashboard")}
                className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="sc-goto-dashboard"
              >
                {IT ? "Vai al mio pannello" : "Open my dashboard"}<ArrowRight className="w-4 h-4 ml-1"/>
              </Button>
            </div>
          )}

          {/* Registration invite (only if not yet registered) */}
          {!accountCreated && canRegister && (
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-primary/10 via-sage/10 to-primary/5 px-6 py-5">
                <div className="uppercase tracking-[0.2em] text-[10px] text-primary font-semibold flex items-center gap-1.5">
                  <Gift className="w-3 h-3"/>{IT ? "Un regalo per te" : "A gift for you"}
                </div>
                <h2 className="font-display text-2xl mt-1">
                  {IT ? "Sblocca i vantaggi HospitalityHub" : "Unlock HospitalityHub perks"}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {IT
                    ? "Crea il tuo account in 5 secondi — usiamo i dati che hai appena fornito."
                    : "Create your account in 5 seconds — we use the data you just provided."}
                </p>
              </div>

              <div className="px-6 py-5 space-y-4">
                <ul className="space-y-2.5">
                  <li className="flex items-start gap-3 text-sm" data-testid="sc-benefit-discount">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Gift className="w-4 h-4 text-primary"/></div>
                    <div>
                      <div className="font-semibold">{IT ? "Sconto 5% sulla prossima prenotazione" : "5% off your next booking"}</div>
                      <div className="text-xs text-muted-foreground">{IT ? "Codice consegnato subito, valido 6 mesi in tutta la rete" : "Code issued instantly, valid 6 months across the network"}</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3 text-sm" data-testid="sc-benefit-guide">
                    <div className="w-8 h-8 rounded-full bg-sage/15 flex items-center justify-center shrink-0"><BookOpen className="w-4 h-4 text-sage"/></div>
                    <div>
                      <div className="font-semibold">{IT ? "Guida digitale della struttura" : "Digital property guide"}</div>
                      <div className="text-xs text-muted-foreground">{IT ? "Orari, wifi, colazione, indicazioni sempre a portata di mano" : "Hours, wifi, breakfast, directions — always on hand"}</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3 text-sm" data-testid="sc-benefit-exclusive">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Sparkles className="w-4 h-4 text-primary"/></div>
                    <div>
                      <div className="font-semibold">{IT ? "Contenuti ed esperienze esclusive" : "Exclusive content & experiences"}</div>
                      <div className="text-xs text-muted-foreground">{IT ? "Offerte partner, itinerari, consigli locali riservati agli iscritti" : "Partner offers, itineraries and local tips just for members"}</div>
                    </div>
                  </li>
                </ul>

                <div className="pt-3 border-t border-border space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[11px]">{IT ? "Nome" : "Name"}</Label>
                      <Input value={info?.booking?.guest_name || `${f.first_name} ${f.last_name}`.trim()} readOnly disabled className="bg-muted/50" data-testid="sc-reg-name"/>
                    </div>
                    <div>
                      <Label className="text-[11px]">Email</Label>
                      <Input value={guestEmail} readOnly disabled className="bg-muted/50 text-xs" data-testid="sc-reg-email"/>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px] flex items-center gap-1"><Lock className="w-3 h-3"/>{IT ? "Scegli una password (min. 8 caratteri)" : "Choose a password (min. 8 chars)"}</Label>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={IT ? "••••••••" : "••••••••"}
                      autoComplete="new-password"
                      minLength={8}
                      data-testid="sc-reg-password"
                    />
                  </div>
                  <Button
                    onClick={doRegister}
                    disabled={registering || password.length < 8}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 lift"
                    size="lg"
                    data-testid="sc-register-btn"
                  >
                    {registering ? "…" : (IT ? "Crea account e attiva lo sconto" : "Create account & unlock discount")}
                  </Button>
                  <p className="text-[10px] text-muted-foreground text-center">
                    {IT ? "Iscrivendoti accetti i Termini e la Privacy. Puoi disiscriverti in qualsiasi momento." : "By signing up you accept the Terms and Privacy. You can opt out any time."}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Already registered as guest */}
          {!accountCreated && alreadyGuest && (
            <div className="bg-card border border-border rounded-2xl p-6 text-center" data-testid="sc-already-registered">
              <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-2">{IT ? "Ben ritrovato!" : "Welcome back!"}</div>
              <p className="text-sm text-muted-foreground mb-4">
                {IT ? "Hai già un account HospitalityHub con questa email. Accedi per vedere le tue prenotazioni." : "You already have a HospitalityHub account with this email. Log in to see your bookings."}
              </p>
              <Link
                to={`/guest/auth?mode=login&email=${encodeURIComponent(guestEmail)}`}
                className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-4 py-2 text-sm font-semibold"
                data-testid="sc-login-link"
              >
                {IT ? "Accedi" : "Log in"}<ArrowRight className="w-4 h-4"/>
              </Link>
            </div>
          )}

          {/* Email exists with different role */}
          {!accountCreated && otherRole && (
            <div className="bg-card border border-border rounded-2xl p-5 text-center text-xs text-muted-foreground" data-testid="sc-other-role">
              {IT
                ? "L'email della prenotazione è già registrata come struttura o partner. Usa il login principale per accedere."
                : "This booking email is already registered as a host or partner. Use the main login to sign in."}
            </div>
          )}

          <div className="text-center">
            <Link to="/" className="text-muted-foreground hover:text-primary text-xs">← {IT ? "Torna alla home" : "Back to home"}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sand/40 to-background pb-16" data-testid="self-checkin-page">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-2xl mx-auto px-6 py-6">
          <div className="flex items-start gap-4">
            {info?.property?.hero_image && <img src={info.property.hero_image} alt={info.property.name} className="w-20 h-20 rounded-lg object-cover shrink-0"/>}
            <div>
              <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3"/>{IT ? "Self check-in · Alloggiati Web" : "Self check-in"}
              </div>
              <h1 className="font-display text-2xl mt-1">{info?.property?.name}</h1>
              <div className="text-xs text-muted-foreground">{info?.booking?.room_name} · {info?.booking?.check_in} → {info?.booking?.check_out} · {info?.booking?.nights} {IT ? "notti" : "nights"}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 pt-6 space-y-4">
        <div className="border border-primary/20 bg-primary/5 rounded-lg p-4">
          <p className="text-sm text-foreground">
            {IT
              ? `Ciao ${info?.booking?.guest_name || ""}! Completa questo modulo per registrare il tuo check-in. È obbligatorio per legge (Polizia di Stato) e ci permette di preparare l'accesso quando arrivi.`
              : `Hello ${info?.booking?.guest_name || ""}! Please fill this form to register your check-in. It's required by Italian law and helps us prepare your access.`}
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">
            {IT ? "🔒 I tuoi dati sono trasmessi in sicurezza e conservati solo per gli obblighi di legge." : "🔒 Your data is transmitted securely and kept only for legal purposes."}
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>{IT ? "Tipo *" : "Type *"}</Label>
              <Select value={f.guest_type} onValueChange={(v) => set("guest_type", v)}>
                <SelectTrigger data-testid="sc-type"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="16">{IT ? "Ospite singolo" : "Single guest"}</SelectItem>
                  <SelectItem value="17">{IT ? "Capo famiglia" : "Family head"}</SelectItem>
                  <SelectItem value="18">{IT ? "Capo gruppo" : "Group leader"}</SelectItem>
                  <SelectItem value="19">{IT ? "Familiare" : "Family member"}</SelectItem>
                  <SelectItem value="20">{IT ? "Membro gruppo" : "Group member"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{IT ? "Sesso *" : "Gender *"}</Label>
              <Select value={f.gender} onValueChange={(v) => set("gender", v)}>
                <SelectTrigger data-testid="sc-gender"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="M">M</SelectItem><SelectItem value="F">F</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>{IT ? "Data di nascita *" : "Date of birth *"}</Label>
              <Input type="date" value={f.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} data-testid="sc-dob" className={fieldErr("date_of_birth") ? "border-destructive" : ""}/>
              {fieldErr("date_of_birth") && <p className="text-[10px] text-destructive mt-1" data-testid="sc-err-dob">{fieldErr("date_of_birth")}</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{IT ? "Cognome *" : "Last name *"}</Label>
              <Input value={f.last_name} onChange={(e) => set("last_name", e.target.value.toUpperCase())} data-testid="sc-last" className={fieldErr("last_name") ? "border-destructive" : ""}/>
              {fieldErr("last_name") && <p className="text-[10px] text-destructive mt-1" data-testid="sc-err-last">{fieldErr("last_name")}</p>}
            </div>
            <div>
              <Label>{IT ? "Nome *" : "First name *"}</Label>
              <Input value={f.first_name} onChange={(e) => set("first_name", e.target.value.toUpperCase())} data-testid="sc-first" className={fieldErr("first_name") ? "border-destructive" : ""}/>
              {fieldErr("first_name") && <p className="text-[10px] text-destructive mt-1" data-testid="sc-err-first">{fieldErr("first_name")}</p>}
            </div>
          </div>
          <div>
            <Label>{IT ? "Stato di nascita *" : "Country of birth *"}</Label>
            <StatoAutocomplete value={f.country_of_birth_code} onChange={(s) => set("country_of_birth_code", s ? s.code : "")} testid="sc-country-birth"/>
            {fieldErr("country_of_birth_code") && <p className="text-[10px] text-destructive mt-1" data-testid="sc-err-country">{fieldErr("country_of_birth_code")}</p>}
          </div>
          {isItalianBorn && (
            <div className="grid grid-cols-2 gap-3 pl-3 border-l-2 border-primary/30">
              <div>
                <Label>{IT ? "Comune di nascita" : "Municipality"}</Label>
                <ComuneAutocomplete value={f.comune_of_birth_code} onChange={(c) => { set("comune_of_birth_code", c ? c.code : ""); if (c) set("province_of_birth", c.prov); }} testid="sc-comune"/>
                {fieldErr("comune_of_birth_code") && <p className="text-[10px] text-destructive mt-1" data-testid="sc-err-comune">{fieldErr("comune_of_birth_code")}</p>}
              </div>
              <div>
                <Label>{IT ? "Provincia" : "Province"}</Label>
                <Select value={f.province_of_birth} onValueChange={(v) => set("province_of_birth", v)}>
                  <SelectTrigger data-testid="sc-prov" className={fieldErr("province_of_birth") ? "border-destructive" : ""}><SelectValue placeholder="MI"/></SelectTrigger>
                  <SelectContent className="max-h-64">{PROVINCE_CODES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
                {fieldErr("province_of_birth") && <p className="text-[10px] text-destructive mt-1" data-testid="sc-err-prov">{fieldErr("province_of_birth")}</p>}
              </div>
            </div>
          )}
          <div>
            <Label>{IT ? "Cittadinanza *" : "Citizenship *"}</Label>
            <StatoAutocomplete value={f.citizenship_code} onChange={(s) => set("citizenship_code", s ? s.code : "")} testid="sc-citizenship"/>
            {fieldErr("citizenship_code") && <p className="text-[10px] text-destructive mt-1" data-testid="sc-err-citiz">{fieldErr("citizenship_code")}</p>}
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-5 space-y-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{IT ? "Documento di identità" : "Identity document"}</div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>{IT ? "Tipo *" : "Type *"}</Label>
              <Select value={f.doc_type} onValueChange={(v) => set("doc_type", v)}>
                <SelectTrigger data-testid="sc-doc-type"><SelectValue/></SelectTrigger>
                <SelectContent>{docTypes.map((d) => <SelectItem key={d.code} value={d.code}>{d.code}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{IT ? "Numero *" : "Number *"}</Label>
              <Input value={f.doc_number} onChange={(e) => set("doc_number", e.target.value.toUpperCase())} className={`font-mono ${fieldErr("doc_number") ? "border-destructive" : ""}`} data-testid="sc-doc-num"/>
              {fieldErr("doc_number") && <p className="text-[10px] text-destructive mt-1" data-testid="sc-err-doc-num">{fieldErr("doc_number")}</p>}
            </div>
            <div>
              <Label>{IT ? "Rilasciato a *" : "Issued at *"}</Label>
              <ComuneAutocomplete value={f.doc_place_code} onChange={(c) => set("doc_place_code", c ? c.code : "")} placeholder={IT ? "Cerca comune…" : "Search municipality…"} testid="sc-doc-place"/>
              {fieldErr("doc_place_code") && <p className="text-[10px] text-destructive mt-1" data-testid="sc-err-doc-place">{fieldErr("doc_place_code")}</p>}
              {!f.doc_place_code && (
                <button
                  type="button"
                  onClick={() => {
                    const c = window.prompt(IT ? "Inserisci codice ISTAT del comune (es. F205)" : "Enter ISTAT municipality code (e.g. F205)");
                    if (c) set("doc_place_code", c.trim().toUpperCase().slice(0, 9));
                  }}
                  className="text-[10px] text-muted-foreground hover:text-primary underline mt-1"
                  data-testid="sc-doc-place-manual-toggle"
                >
                  {IT ? "Comune non trovato? Inserisci il codice ISTAT manualmente" : "Municipality not found? Enter ISTAT code manually"}
                </button>
              )}
            </div>
          </div>

          {/* Photo upload */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { key: "doc_photo_front_url", label: IT ? "Foto documento (fronte)" : "Doc front", icon: Camera },
              { key: "doc_photo_back_url", label: IT ? "Foto documento (retro)" : "Doc back", icon: Camera },
              { key: "selfie_url", label: IT ? "Selfie (opzionale)" : "Selfie (optional)", icon: Camera },
            ].map(({ key, label, icon: Icon }) => (
              <label key={key} className={`border-2 border-dashed rounded-lg p-3 cursor-pointer text-center transition-all ${f[key] ? "border-sage bg-sage/5" : "border-border hover:border-primary/40"}`} data-testid={`sc-upload-${key}`}>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(key, e.target.files?.[0])}/>
                {f[key] ? (
                  <div className="text-xs">
                    <Check className="w-5 h-5 mx-auto text-sage mb-1"/>
                    {IT ? "Caricata" : "Uploaded"}
                    <img src={f[key]} alt="" className="w-full h-20 object-cover rounded mt-1"/>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">
                    {uploading[key] ? <span>…</span> : <Icon className="w-5 h-5 mx-auto mb-1"/>}
                    <div>{label}</div>
                  </div>
                )}
              </label>
            ))}
          </div>
        </div>

        {!isValid && (
          <div className="border border-amber-500/40 bg-amber-500/10 rounded-md px-3 py-2 text-[11px] text-amber-800 flex items-start gap-2" data-testid="sc-validation-summary">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
            <span>
              {IT
                ? `Mancano ${Object.keys(valErr).length} campi obbligatori Alloggiati Web. Il pulsante di invio si attiva quando la scheda è completa.`
                : `${Object.keys(valErr).length} required Alloggiati Web fields missing. The submit button unlocks when the record is complete.`}
            </span>
          </div>
        )}
        <Button onClick={submit} disabled={saving || !isValid} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 lift disabled:opacity-50" size="lg" data-testid="sc-submit">
          {saving ? "…" : isValid ? (IT ? "Invia i miei dati" : "Submit my data") : (IT ? "Completa i campi per continuare" : "Complete the fields to continue")}
        </Button>
        <p className="text-[10px] text-muted-foreground text-center">
          {IT ? "Inviando confermi che i dati sono veritieri (art. 76 D.P.R. 445/2000)." : "By submitting you confirm the data is truthful."}
        </p>
      </div>
    </div>
  );
}
